import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { CountryISO, SearchCountryField } from "ngx-intl-tel-input";
import { SlickCarouselComponent } from "ngx-slick-carousel";
import { Subscription } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { ResponsiveSlideHelper } from "../../../../core/_helpers";
import {
  ACADEMY_PATH,
  resolveAcademyRoute
} from "../../../../core/config/academy-routes";
import { DemoDialogService } from "../../../../core/services/dialog";
import { ProductService } from "../../../../core/services/products.service";
import { SiilaTranslationService } from "../../../../core/services/siila-translation.service";
import { WindowRef } from "../../../../window-ref.service";
import { I18nService } from "../../../services";
import { CaptchaService } from "../../../services/captcha.service";
import { MetaTagService } from "../../../services/meta-tag.service";
import { UrlCanonicalService } from "../../../services/url-canonical.service";
import { action, page } from "../../../utils/params";
import { DemoDialogComponent } from "../../dialogs";

/** One card in the Academy courses carousel. */
export interface AcademyCourseCard {
  img: string;
  alt: string;
  /** i18n key, not a rendered string — the template pipes it through translate. */
  title: string;
  description: string;
  /** Which course the "learn more" action opens (`br`, `br-advanced`, `mx`, `retail`). */
  code: string;
  /** Inline `width:` style the carousel sizes the slide with. */
  width: string;
  buttonLabel?: string;
  showButton?: boolean;
}

/** One checked line in the "about the course" list. */
export interface AcademyAboutItem {
  icon: string;
  /** i18n key. */
  title: string;
}

@Component({
  selector: "app-siila-academy",
  templateUrl: "./siila-academy.component.html",
  styleUrls: [
    "./siila-academy.component.scss",
    "../market-analytics/market-analytics.component.scss",
    "../fii-data-pro/fii-data-pro.component.scss",
    "../about/about.component.scss",
    "../product-list/product-list.component.scss"
  ]
})
export class SiilaAcademyComponent implements OnInit {
  @ViewChild("slickCarousel", { static: true })
  carousel: SlickCarouselComponent;
  translatePathStrategy: string;

  currentCode: string;
  currentCodeMx = "mx";
  currentCodeBr = "br";
  aboutPath: string;
  aboutList: AcademyAboutItem[] = [];
  isMobile: boolean;
  subscription: Subscription;
  action = action;
  form: FormGroup;
  lang: string;
  formEbook: FormGroup;
  routerSub: Subscription;
  courseLatam: AcademyCourseCard[];
  courseCarousel: AcademyCourseCard[];
  sponsorEmail = "gabriel.lucas@siila.com.br";
  slideConfig: typeof ResponsiveSlideHelper.DEFAULT_SLIDE_CONFIG_ACADEMY =
    ResponsiveSlideHelper.DEFAULT_SLIDE_CONFIG_ACADEMY;
  slideCurrentIndex = 1;
  imgAcademyAlt: string;
  classesAlt: string;
  officebr: string;
  officemx: string;
  industrialmx: string;
  industrialbr: string;
  personmobile: string;
  public screenWidth: number;
  public handsetMode: boolean = false;
  imgCaptcha = "";
  textCaptcha: string;
  errorCaptcha = false;

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  preferredCountries: CountryISO[] = [];

  private platformToCountry: { [key: string]: CountryISO } = {
    br: CountryISO.Brazil,
    mx: CountryISO.Mexico,
    co: CountryISO.Colombia
  };
  retailphotoacAlt: string;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private responsive: BreakpointObserver,
    private demo: DemoDialogService,
    private fb: FormBuilder,
    private productService: ProductService,
    private i18: I18nService,
    private snackBar: MatSnackBar,
    private siilaTranslationService: SiilaTranslationService,
    private metatagservice: MetaTagService,
    private route: ActivatedRoute,
    private window: WindowRef,
    private captchaService: CaptchaService,
    private urlCanonical: UrlCanonicalService,
    private router: Router
  ) {
    this.currentCode = environment.platformId;
    this.translatePathStrategy = `analytics.${this.currentCode}.strategy`;
    this.lang = this.siilaTranslationService.getCurrentLanguage();
    this.setPreferredCountries();

    this.aboutList = [...Array(5).keys()].map(i => {
      this.aboutPath = `academy.${this.currentCode}.academy.courses.about.course-list.`;
      return {
        icon: "/assets/icons/check.svg",
        title: `${this.aboutPath}text${i + 1}`
      };
    });

    this.form = this.fb.group({
      fullName: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      description: ["", Validators.required],
      page: ["academy"],
      action: [this.i18.get("global.newsForm.action")],
      phone: [null, Validators.required],
      inputCaptcha: [""]
    });

    this.formEbook = this.fb.group({
      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      phoneNumber: [""],
      email: ["", [Validators.required, Validators.email]],
      company: ["", Validators.required],
      position: ["", Validators.required],
      requestType: ["EBOOK"]
    });

    this.screenWidth = this.window.nativeWindow.innerWidth;
    this.screenWidth < 1000
      ? (this.handsetMode = true)
      : (this.handsetMode = false);
  }

  ngOnInit(): void {
    this.generateCaptcha();
    this.personmobile = this.siilaTranslationService.getImgSEOTranslation(
      "academy.personmobile"
    );
    this.imgAcademyAlt = this.siilaTranslationService.getImgSEOTranslation(
      "academy.main"
    );
    this.classesAlt = this.siilaTranslationService.getImgSEOTranslation(
      "academy.classes"
    );
    this.officebr = this.siilaTranslationService.getImgSEOTranslation(
      "academy.cursos.officebr"
    );
    this.officemx = this.siilaTranslationService.getImgSEOTranslation(
      "academy.cursos.officemx"
    );
    this.industrialmx = this.siilaTranslationService.getImgSEOTranslation(
      "academy.cursos.industrialmx"
    );
    this.industrialbr = this.siilaTranslationService.getImgSEOTranslation(
      "academy.cursos.industrialbr"
    );
    this.retailphotoacAlt = this.siilaTranslationService.getImgSEOTranslation(
      "academy.retailphotoac"
    );
    this.routerSub = this.route.paramMap.subscribe(params => {
      if (!this.applyAcademyUrlRules(params.get("language"))) {
        return;
      }
      this.metatagservice.setAcademyMetaTags();
    });
    this.subscription = this.responsive
      .observe(Breakpoints.XSmall)
      .subscribe(result => {
        this.isMobile = result.matches;
        this._changeDetectorRef.detectChanges();
        if (this.currentCode == "br") {
          this.courseCarousel = [
            {
              img: this.getBrAdvancedSlideImage(),
              alt: this.imgAcademyAlt,
              title: `academy.${this.currentCode}.academy.courses.card-info.titleAdvanced`,
              description: `academy.${this.currentCode}.academy.courses.card-info.descriptionAdvanced`,
              code: "br-advanced",
              buttonLabel: `academy.${this.currentCode}.academy.courses.card-info.buttonAdvanced`,
              showButton: true,
              width: this.isMobile ? "width:54%" : "width:556px"
            },
            {
              img: "/assets/img/academy-list/ACADEMY-OFFICES-BR.jpg",
              alt: this.retailphotoacAlt,
              title: `academy.${this.currentCode}.academy.courses.card-info.title`,
              description: `academy.${this.currentCode}.academy.courses.card-info.description`,
              code: "br",
              showButton: false,
              width: this.isMobile ? "width:54%" : "width:556px"
            },
            {
              img: "/assets/img/academy-list/ACADEMY-INDUSTRIAL-BR.jpg",
              alt: this.retailphotoacAlt,
              title: `academy.${this.currentCode}.academy.courses.card-info.title2`,
              description: `academy.${this.currentCode}.academy.courses.card-info.description2`,
              code: "br",
              showButton: false,
              width: this.isMobile ? "width:54%" : "width:556px"
            }
          ];
        }
        if (this.currentCode == "mx") {
          this.courseCarousel = [
            {
              img: "/assets/img/academy-list/RETAIL PHOTO ACADEMY.png",
              alt: this.retailphotoacAlt,
              title: `academy.${this.currentCode}.academy.courses.card-info.title3`,
              description: `academy.${this.currentCode}.academy.courses.card-info.description3`,
              code: "retail",
              width: this.isMobile ? "width:54%" : "width:556px"
            },
            {
              img: "/assets/img/academy-list/ACADEMY-OFFICES-mx.jpg",
              alt: this.officemx,
              title: `academy.${this.currentCode}.academy.courses.card-info.title`,
              description: `academy.${this.currentCode}.academy.courses.card-info.description`,
              code: "mx",
              width: this.isMobile ? "width:54%" : "width:556px"
            },
            {
              img: "/assets/img/academy-list/ACADEMY-INDUSTRIAL-mx.jpg",
              alt: this.industrialmx,
              title: `academy.${this.currentCode}.academy.courses.card-info.title2`,
              description: `academy.${this.currentCode}.academy.courses.card-info.description2`,
              code: "mx",
              width: this.isMobile ? "width:54%" : "width:556px"
            }
          ];
        }
        if (this.currentCode == "latam") {
          this.courseLatam = [
            {
              img: this.getBrAdvancedSlideImage(),
              title: `academy.${this.currentCodeBr}.academy.courses.card-info.titleAdvanced`,
              alt: this.imgAcademyAlt,
              description: `academy.${this.currentCodeBr}.academy.courses.card-info.descriptionAdvanced`,
              code: "br-advanced",
              buttonLabel: `academy.${this.currentCodeBr}.academy.courses.card-info.buttonAdvanced`,
              showButton: true,
              width: this.isMobile ? "width:45%" : "width:556px"
            },
            {
              img: "/assets/img/academy-list/RETAIL PHOTO ACADEMY.png",
              alt: this.retailphotoacAlt,
              title: `academy.${this.currentCode}.academy.courses.card-info.title3`,
              description: `academy.${this.currentCode}.academy.courses.card-info.description3`,
              code: "retail",
              width: this.isMobile ? "width:54%" : "width:556px"
            },
            {
              img: "/assets/img/academy-list/ACADEMY-OFFICES-BR.jpg",
              title: `academy.${this.currentCodeBr}.academy.courses.card-info.title`,
              alt: this.retailphotoacAlt,
              description: `academy.${this.currentCodeBr}.academy.courses.card-info.description`,
              code: "br",
              showButton: false,
              width: this.isMobile ? "width:54%" : "width:556px"
            },
            {
              img: "/assets/img/academy-list/ACADEMY-INDUSTRIAL-BR.jpg",
              title: `academy.${this.currentCodeBr}.academy.courses.card-info.title2`,
              alt: this.retailphotoacAlt,
              description: `academy.${this.currentCodeBr}.academy.courses.card-info.description2`,
              code: "br",
              showButton: false,
              width: this.isMobile ? "width:45%" : "width:556px"
            },
            {
              img: "/assets/img/academy-list/ACADEMY-OFFICES-mx.jpg",
              alt: this.officemx,
              title: `academy.${this.currentCodeMx}.academy.courses.card-info.title`,
              description: `academy.${this.currentCodeMx}.academy.courses.card-info.description`,
              code: "mx",
              width: this.isMobile ? "width:54%" : "width::556px"
            },
            {
              img: "/assets/img/academy-list/ACADEMY-INDUSTRIAL-mx.jpg",
              alt: this.industrialmx,
              title: `academy.${this.currentCodeMx}.academy.courses.card-info.title2`,
              description: `academy.${this.currentCodeMx}.academy.courses.card-info.description2`,
              code: "mx",
              width: this.isMobile ? "width:45%" : "width:556px"
            }
          ];
          this.courseCarousel = this.courseLatam;
        }
      });
  }

  /**
   * Bring an `/academy…` URL to the one page this deployment publishes at it,
   * and answer 404 when it publishes none. Returns false when the request was
   * answered with a 404, so the caller skips the meta tags of a page that is not
   * being shown.
   *
   * `academy/lang/:language` matches ANY segment, so without this every spelling
   * — an unsupported language, the default language's own suffix, plain junk —
   * rendered the same page at a second indexable URL. See `academy-routes.ts`
   * for the rule table and why the default-language suffix redirects rather than
   * 404s.
   */
  private applyAcademyUrlRules(rawLangSegment: string | null): boolean {
    const sts = this.siilaTranslationService;
    const segmentLanguage = rawLangSegment
      ? sts.getLanguageFromSuffixCode(rawLangSegment)
      : undefined;
    // Not `action`: the component imports a constant of that name from
    // `utils/params` and re-exports it as a field for the template.
    const routeAction = resolveAcademyRoute({
      platformId: environment.platformId,
      rawLangSegment,
      segmentLanguage,
      defaultLanguage: sts.getDefaultLanguage()
    });

    if (routeAction === "notFound") {
      this.navigateToNotFoundPage();
      return false;
    }

    // `render` stays on the URL it arrived at (already canonical); `redirect`
    // moves to the clean default-language path. One call covers both: `apply`
    // no-ops on a URL that is already canonical (clearing any stale 301 pair a
    // previous page left behind) and otherwise corrects the address bar with
    // `replaceState` while emitting the Prerender.io 301 the crawler needs.
    const targetLanguage =
      routeAction === "render" && segmentLanguage
        ? segmentLanguage
        : sts.getDefaultLanguage();
    const canonicalUrl = this.urlCanonical.apply(
      sts.buildLocalizedPath(ACADEMY_PATH, targetLanguage)
    );
    this.metatagservice.addCanonicalTag(canonicalUrl);
    return true;
  }

  /**
   * Terminal 404 path. Routes IN-APP so the `prerender-status-code: 404`
   * `PageNotFoundComponent` emits lands in the same document the crawler is
   * rendering — a hard navigation would lose it and snapshot a soft-200.
   */
  private navigateToNotFoundPage(): void {
    this.router.navigateByUrl("/404", {
      state: { badURL: window.location.href }
    });
  }

  ngOnDestroy(): void {
    if (this.routerSub) this.routerSub.unsubscribe();
  }

  openContactDialog(action) {
    this.demo.open(
      DemoDialogComponent,
      false,
      page.academy,
      action,
      "",
      "ACADEMY",
      false
    );
  }

  submitMessage() {
    this.validateCaptcha();
    if (!this.errorCaptcha) {
      let formValue = this.form.value;
      this.form.reset();
      let message = this.i18.get("global.newsForm.message");
      let action = this.i18.get("global.newsForm.action");
      let errorMsj = this.i18.get("global.newsForm.errorMsj");
      this.productService.demoRequest(formValue).subscribe(
        response => {
          this.snackBar.open(message, action);
          this.generateCaptcha();
        },
        () => this.snackBar.open(errorMsj)
      );
    }
  }

  generateCaptcha() {
    this.captchaService.generateCaptcha();
    this.imgCaptcha = this.captchaService.imgCaptcha;
    this.textCaptcha = this.captchaService.textCaptcha;
  }

  validateCaptcha() {
    this.errorCaptcha =
      this.form.controls.inputCaptcha.value != this.textCaptcha ||
      this.form.controls.inputCaptcha.value == "";
    return this.errorCaptcha;
  }

  private setPreferredCountries(): void {
    const allCountries = [
      CountryISO.Brazil,
      CountryISO.Mexico,
      CountryISO.Colombia,
      CountryISO.UnitedStates
    ];
    const currentCountry = this.platformToCountry[this.currentCode];
    if (currentCountry) {
      this.preferredCountries = [
        currentCountry,
        ...allCountries.filter(c => c !== currentCountry)
      ];
    } else {
      this.preferredCountries = allCountries;
    }
  }

  imgContactUs() {
    let img = "/assets/img/products-list/contactAcademy.png";

    return img;
  }

  imgAcademy() {
    if (this.currentCode == "br") {
      return this.lang == "en"
        ? "/assets/img/academy-list/BR Academy Laptop EN-1.png"
        : "/assets/img/academy-list/BR Academy Laptop PT-1.png";
    }

    let img = "/assets/img/products-list/";
    if (
      this.currentCode == "mx" ||
      this.currentCode == "co" ||
      this.currentCode == "latam"
    ) {
      if (this.lang == "en") {
        img = img + "2-3 ACADEMY ENGLISH.png";
      } else {
        img = img + "23 ACADEMY ESPANISH.png";
      }
    } else {
      if (this.lang == "en") {
        img = img + "2-3 ACADEMY ENGLISH.png";
      } else {
        img = img + "23 ACADEMY PORTUGUES.png";
      }
    }

    return img;
  }

  imgAcademyMobile() {
    let img = "/assets/img/academy-list/";
    if (
      this.currentCode == "mx" ||
      this.currentCode == "co" ||
      this.currentCode == "latam"
    ) {
      if (this.lang == "en") {
        img = img + "ACADEMY ENGLISH.png";
      } else {
        img = img + "ACADEMY ESPANISH.png";
      }
    } else {
      if (this.lang == "en") {
        img = img + "ACADEMY ENGLISH.png";
      } else {
        img = img + "ACADEMY PORTUGUES.png";
      }
    }

    return img;
  }

  getBrAdvancedSlideImage() {
    return this.lang == "en"
      ? "/assets/img/academy-list/BR Academy Slide Image EN.png"
      : "/assets/img/academy-list/BR Academy Slide Image PT.png";
  }

  getAcademySponsorImage() {
    return this.lang == "en"
      ? "/assets/img/academy-list/BR Academy Sponsor EN.png"
      : "/assets/img/academy-list/BR Academy Sponsor PT.png";
  }

  openSponsorEmail() {
    window.location.href = `mailto:${this.sponsorEmail}`;
  }

  downloadFormIndex() {
    this.formEbook.controls.firstName.setValue("ANONYMOUS");
    this.formEbook.controls.lastName.setValue("ANONYMOUS");
    this.formEbook.controls.phoneNumber.setValue("ANONYMOUS");
    this.formEbook.controls.email.setValue("anonymous@localhost");
    this.formEbook.controls.company.setValue("ANONYMOUS");
    this.formEbook.controls.position.setValue("ANONYMOUS");
    this.formEbook.controls.requestType.setValue("EBOOK");
    let formValue = this.formEbook.value;
    this.formEbook.reset();
    let message = this.i18.get("global.newsForm.download");
    let action = this.i18.get("global.newsForm.action");
    let errorMsj = this.i18.get("global.newsForm.errorMsj");
    this.productService.requestFileIndex(formValue).subscribe(
      response => {
        this.productService.downloadPdf(response.resourceUrl);
        if (response.success === false) {
          this.snackBar.open(errorMsj, action);
        } else {
          this.snackBar.open(message, action);
        }
      },
      () => {
        this.snackBar.open(errorMsj, action);
      }
    );
  }

  openCouse(course?: string) {
    if (course == "br-advanced") {
      window.open(
        "https://www.sympla.com.br/evento-online/siila-academy-advanced/3379623?algoliaID=0670978560f2b07348e17e2bd0452db4",
        "_blank"
      );
      return;
    }

    if (this.currentCode == "mx" || course == "mx" || course == "retail") {
      if (course == "retail") {
        window.open("https://mailchi.mp/siila/academy-2026retail", "_blank");
        return;
      }
      if (this.lang == "en") {
        window.open("https://mailchi.mp/siila/academy-advanced", "_blank");
      } else {
        window.open("https://mailchi.mp/siila/academy-advanced", "_blank");
      }
    } else {
      if (this.lang == "en") {
        window.open(
          "https://www.sympla.com.br/evento-online/siila-academy-advanced/3379623?referrer=www.linkedin.com&referrer=www.linkedin.com&referrer=mailchi.mp&referrer=mailchi.mp",
          "_blank"
        );
      } else {
        window.open(
          "https://www.sympla.com.br/evento-online/siila-academy-advanced/3379623?referrer=www.linkedin.com&referrer=www.linkedin.com&referrer=mailchi.mp&referrer=mailchi.mp",
          "_blank"
        );
      }
    }
  }

  changeSlideNext(slide) {
    const totalSlides = this.courseCarousel ? this.courseCarousel.length : 0;
    if (!totalSlides) {
      slide.slickNext();
      return;
    }

    if (this.isMobile) {
      const lastIndex = totalSlides - 1;
      if (slide.currentIndex >= lastIndex) {
        slide.slickGoTo(0);
      } else {
        slide.slickNext();
      }
    } else {
      const lastPageIndex = Math.max(totalSlides - 2, 0);
      if (slide.currentIndex >= lastPageIndex) {
        slide.slickGoTo(0);
      } else {
        slide.slickNext();
      }
    }
  }

  changeSlideBack(slide) {
    slide.slickPrev();
  }
  /**
   * @param  {} event
   */
  slickInit(event) {}

  /**
   * @param  {} event
   */
  afterChange(e) {
    const { currentSlide } = e;
    this.slideCurrentIndex = currentSlide;
  }

  /**
   * @param  {} event
   */
  breakpoint(event) {}

  /**
   * @param  {} event
   */
  beforeChange(event) {}

  /**
   * @param  {} event
   */
}
